import { useState, useEffect, useRef, useCallback } from 'react';
import { logWs } from '../store/debugStore.js';

const RECONNECT_INTERVAL = 3000;
const MAX_RECONNECT_DELAY = 30000;

/**
 * @param {object} options
 * @param {string} [options.wsUrl] - explicit signaling URL (from join API).
 *                                   When provided, takes precedence over roomId.
 * @param {string} [options.roomId] - fallback: builds the URL from the room ID.
 * @param {'operator'|'client'} [options.role] - role for the fallback URL; hub
 *                                               defaults bare connections to
 *                                               "client", so missing role makes
 *                                               operators announce themselves as clients.
 * @param {(event: object) => void} onMessage
 * @param {(error: Error) => void} onError
 * @param {(connected: boolean) => void} [onConnectChange]
 */
export function useWebSocket({ wsUrl, roomId, role, onMessage, onError, onConnectChange }) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(/** @type {WebSocket|null} */ (null));
  const reconnectTimerRef = useRef(/** @type {number|null} */ (null));
  const reconnectDelayRef = useRef(RECONNECT_INTERVAL);
  // Stable refs — the effect must NOT fire on callback shape changes, or it
  // tears down live sockets mid-call.
  const sendQueueRef = useRef([]);
  const callbacksRef = useRef({ onMessage, onError });
  const onConnectChangeRef = useRef(onConnectChange);
  // Tracks whether we've ever successfully connected — reconnect notifications
  // only fire when a LIVE connection drops, never on the first attempt.
  const everConnectedRef = useRef(false);

  callbacksRef.current = { onMessage, onError };
  onConnectChangeRef.current = onConnectChange;

  const close = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      // Close with code 4000 to signal intentional disconnect, not a failure.
      try {
        wsRef.current.close(4000, 'client disconnect');
      } catch {
        // Already closed or closing — ignore.
      }
    }
    wsRef.current = null;
    setConnected(false);
    reconnectDelayRef.current = RECONNECT_INTERVAL;
    sendQueueRef.current = [];
  }, []);

  const flushQueue = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const queue = sendQueueRef.current;
      sendQueueRef.current = [];
      for (let i = 0; i < queue.length; i++) {
        wsRef.current.send(queue[i]);
      }
    }
  }, []);

  const send = useCallback((type, payload) => {
    const msg = JSON.stringify({ type, payload });
    logWs('ws-out', { type, payload });
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(msg);
    } else {
      sendQueueRef.current.push(msg);
    }
  }, []);

  // Stable ref — the effect must NOT fire on URL changes, or it tears down a
  // live socket mid-call (the join flow sets wsUrl after the first render).
  const urlRef = useRef(/** @type {string|null} */ (wsUrl));
  urlRef.current = wsUrl;

  // Derive a single boolean: true once we have any URL. This flips from false
  // to true exactly when the join endpoint returns, which is the one moment
  // the effect needs to re-run. Subsequent URL changes keep it true and do
  // NOT re-fire the effect — no tearing down of live sockets.
  const urlReady = Boolean(urlRef.current) || Boolean(roomId);

  useEffect(() => {
    if (!urlReady) return;

    let active = true;
    let heartbeatTimer = /** @type {number|null} */ (null);

    const tryConnect = () => {
      if (!active) return;

      // Guard against stale connections from a previous effect run.
      const existing = wsRef.current;
      if (existing && existing.readyState !== WebSocket.CLOSED) {
        try {
          existing.close(4000, 'replacing');
        } catch {
          // ignore
        }
        wsRef.current = null;
      }

      const token = localStorage.getItem('scb_auth_token');
      const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
      let url = urlRef.current || `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/ws?room=${encodeURIComponent(roomId)}${role ? '&role=' + encodeURIComponent(role) : ''}${tokenParam}`;
      try {
        const parsed = new URL(url);
        // When accessed remotely (e.g. via Tailscale), always route WebSocket
        // through the frontend server host/port (which proxies /ws to backend).
        parsed.host = location.host;
        parsed.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        if (token && !parsed.searchParams.has('token')) {
          parsed.searchParams.set('token', token);
        }
        url = parsed.toString();
      } catch {
        if (location.protocol === 'http:' && url.startsWith('wss://')) {
          url = 'ws://' + url.slice(6);
        }
      }
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        everConnectedRef.current = true;
        reconnectDelayRef.current = RECONNECT_INTERVAL;
        flushQueue();
        onConnectChangeRef.current?.(true);
        // The room join is handled by the HTTP join endpoint (api.clientJoin /
        // api.operatorJoin), not over WebSocket. Sending a "join" signaling
        // message would be rejected by the mediasoup handler, so we only start
        // the heartbeat here.
        heartbeatTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 15000);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          logWs('ws-in', message);
          callbacksRef.current.onMessage(message);
        } catch (err) {
          console.error('[ws] malformed message', err, event.data);
        }
      };

      ws.onerror = () => {
        callbacksRef.current.onError(new Error('WebSocket error occurred'));
      };

      ws.onclose = (event) => {
        setConnected(false);
        if (heartbeatTimer !== null) {
          clearInterval(heartbeatTimer);
          heartbeatTimer = null;
        }
        wsRef.current = null;
        // Only notify "disconnected — reconnecting" when a previously live
        // connection drops. A first-attempt failure is the join failure, not
        // a disconnect, and gets reported there instead.
        if (everConnectedRef.current) {
          onConnectChangeRef.current?.(false);
        }

        // Intentional disconnects (code 4000) must not trigger reconnect.
        if (active && event.code !== 4000) {
          const delay = Math.min(reconnectDelayRef.current, MAX_RECONNECT_DELAY);
          reconnectDelayRef.current = Math.min(
            reconnectDelayRef.current * 1.5,
            MAX_RECONNECT_DELAY,
          );
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            tryConnect();
          }, delay);
        }
      };
    };

    tryConnect();

    return () => {
      active = false;
      if (heartbeatTimer !== null) {
        clearInterval(heartbeatTimer);
      }
      close();
    };
  // urlReady flips false → true once the join endpoint returns and then never
  // flips back, so the effect runs exactly twice at most: the mount check
  // (early-return) and the first time a URL is available.
  }, [roomId, close, urlReady]);

  return { send, connected };
}
